<template>
    <div>
        <!-- Photo Dialog -->
        <v-dialog v-model="showPhotoDialog"
                  max-width="600">
            <v-card :title="t('photo')">
                <v-card-text>
                    <v-carousel v-if="selectedPhotos.length > 1"
                                v-model="currentPhotoIndex"
                                height="400"
                                hide-delimiter-background
                                show-arrows="hover">
                        <v-carousel-item v-for="(photo, index) in selectedPhotos"
                                         :key="index">
                            <v-img :src="photo"
                                   height="100%"
                                   width="100%"
                                   contain />
                        </v-carousel-item>
                    </v-carousel>
                    <v-img v-else
                           :src="selectedPhotos[0]"
                           height="400"
                           width="100%"
                           contain />
                    <div v-if="selectedPhotos.length > 1" class="text-center mt-2">
                        <span class="text-caption">{{ currentPhotoIndex + 1 }} / {{ selectedPhotos.length }}</span>
                    </div>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn :text="t('close')"
                           variant="plain"
                           @click="showPhotoDialog = false"></v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <BaseGrid ref="baseGrid"
                  :title="t('assetDataTable')"
                  :headers="headers"
                  :query-builder="queryBuilder"
                  :filter-context="filterContext"
                  :default-sort="[{ key: 'assetRefId', order: 'asc' }]"
                  item-value="id"
                  @items-loaded="handleItemsLoaded">
            <!-- Desktop Row -->
            <template #desktop-row="{ item }">
                <tr style="height: 96px!important;">
                    <td v-for="header in headers" :key="header.key">
                        <!-- Asset ID + lifecycle record count -->
                        <template v-if="header.key === 'assetRefId'">
                            <div class="d-flex flex-column align-start ga-1">
                                <v-chip label variant="text" class="px-1">
                                    <a @click.prevent="openItemUrl(item)"
                                       href="#"
                                       style="color: inherit;">
                                        {{ item.assetRefId || item.assetClientId || emptyValue }}
                                    </a>
                                </v-chip>
                                <v-tooltip :text="t('itemsClosed', { closed: item.closedCount, total: item.itemCount })"
                                           location="top">
                                    <template #activator="{ props: tip }">
                                        <span v-bind="tip" class="text-caption text-medium-emphasis">
                                            {{ t('itemsCount', { count: item.itemCount }) }}
                                        </span>
                                    </template>
                                </v-tooltip>
                            </div>
                        </template>

                        <!-- Mini Map -->
                        <template v-else-if="header.key === 'miniMap'">
                            <MiniMap v-if="item.drawingThumbnail || item.drawingUrl"
                                     :thumbnail-url="item.drawingThumbnail"
                                     :x="pinFor(item).x"
                                     :y="pinFor(item).y"
                                     :pin-units="pinFor(item).units"
                                     :drawing-title="item.drawingTitle"
                                     :drawing-url="item.drawingUrl"
                                     :location-title="item.locationTitle" />
                            <div v-else class="mini-map-placeholder"></div>
                        </template>

                        <!-- Photo -->
                        <template v-else-if="header.key === 'photo'">
                            <div class="d-flex justify-center">
                                <v-img v-if="item.photo || item.responsePhoto"
                                       :src="item.photo || item.responsePhoto"
                                       width="64"
                                       height="64"
                                       :aspect-ratio="1"
                                       contain
                                       eager
                                       class="rounded cursor-pointer asset-photo"
                                       @click="openPhotoDialog(item)" />
                                <span v-else class="text-caption text-disabled">{{ emptyValue }}</span>
                            </div>
                        </template>

                        <!-- Drawing / Location -->
                        <template v-else-if="header.key === 'drawingTitle'">
                            <div class="d-flex flex-column align-start" style="width: 180px;">
                                <div class="text-body-2">{{ item.drawingTitle || emptyValue }}</div>
                                <div class="text-caption text-medium-emphasis">{{ item.locationTitle }}</div>
                            </div>
                        </template>

                        <!-- Created by / on -->
                        <template v-else-if="header.key === 'createdByName'">
                            <div class="d-flex flex-column align-start" style="width: 150px;">
                                <div class="text-body-2">{{ item.createdByName || emptyValue }}</div>
                                <div class="text-caption text-medium-emphasis">{{ formatDate(item.createdDate) }}</div>
                            </div>
                        </template>

                        <!-- Category / System / Type -->
                        <template v-else-if="header.key === 'assetSystem'">
                            <div class="d-flex flex-column align-start" style="width: 200px;">
                                <div class="text-subtitle-2">{{ item.assetSystem || emptyValue }}</div>
                                <div class="text-body-2">{{ item.assetType }}</div>
                                <div class="text-caption text-medium-emphasis">{{ item.assetCategory }}</div>
                            </div>
                        </template>

                        <!-- Manufacturer / Model / Serial -->
                        <template v-else-if="header.key === 'manufacturer'">
                            <div class="d-flex flex-column align-start" style="width: 180px;">
                                <div class="text-subtitle-2">{{ item.manufacturer || emptyValue }}</div>
                                <div class="text-body-2">{{ item.model }}</div>
                                <div class="text-caption text-medium-emphasis">{{ item.serialNumber }}</div>
                            </div>
                        </template>

                        <template v-else-if="header.key === 'responsibleParty'">
                            <div class="d-flex flex-column align-start ga-1" style="width: 150px;">
                                <span class="text-body-2">{{ item.responsibleParty || emptyValue }}</span>
                                <v-chip v-if="isPriority(item)"
                                        color="warning"
                                        variant="tonal"
                                        size="x-small"
                                        label>
                                    {{ t('priority') }}
                                </v-chip>
                            </div>
                        </template>

                        <!-- Next inspection with overdue emphasis -->
                        <template v-else-if="header.key === 'nextInspectionDate'">
                            <div class="d-flex flex-column align-start ga-1" style="width: 140px;">
                                <template v-if="item.nextInspectionDate">
                                    <v-chip :color="item.isOverdue ? overdueColor : undefined"
                                            :variant="item.isOverdue ? 'tonal' : 'flat'"
                                            size="small"
                                            label>
                                        {{ formatDate(item.nextInspectionDate) }}
                                    </v-chip>
                                    <span class="text-caption"
                                          :style="item.isOverdue ? { color: overdueColor } : null">
                                        {{ daysText(item) }}
                                    </span>
                                </template>
                                <span v-else class="text-disabled">{{ emptyValue }}</span>
                            </div>
                        </template>

                        <template v-else-if="header.key === 'latestInspectionDate'">
                            <span class="text-body-2">{{ formatDate(item.latestInspectionDate) || emptyValue }}</span>
                        </template>

                        <template v-else-if="header.key === 'installationCost'">
                            <span class="text-body-2">
                                {{ item.installationCost === null ? emptyValue : formatCurrency(item.installationCost) }}
                            </span>
                        </template>

                        <template v-else-if="header.key === 'rating'">
                            <div class="d-flex flex-column align-start" style="width: 110px;">
                                <span class="text-body-2">{{ item.rating || emptyValue }}</span>
                                <span v-if="item.passRate !== null" class="text-caption text-medium-emphasis">
                                    {{ formatPassRate(item.passRate) }}
                                </span>
                            </div>
                        </template>

                        <template v-else-if="header.key === 'passRate'">
                            <span class="text-body-2">{{ formatPassRate(item.passRate) }}</span>
                        </template>

                        <!-- Status -->
                        <template v-else-if="header.key === 'statusTitle'">
                            <div class="d-flex flex-column align-start ga-1" style="width: 160px;">
                                <v-chip v-if="item.statusTitle"
                                        :style="{ backgroundColor: item.statusColor, color: 'white' }"
                                        size="small"
                                        label>
                                    {{ item.statusTitle }}
                                </v-chip>
                                <span v-if="item.isCommissioned" class="text-caption text-medium-emphasis">
                                    {{ t('commissioned') }}
                                </span>
                            </div>
                        </template>

                        <template v-else>
                            <span class="text-body-2">{{ item[header.key] || emptyValue }}</span>
                        </template>
                    </td>
                </tr>
            </template>

            <!-- Mobile Item -->
            <template #mobile-item="{ item }">
                <v-card class="mb-2" variant="flat">
                    <v-card-title class="d-flex justify-space-between align-center">
                        <v-chip label variant="text" class="px-1">
                            <a @click.prevent="openItemUrl(item)"
                               href="#"
                               style="color: inherit;">
                                {{ item.assetRefId || item.assetClientId || emptyValue }}
                            </a>
                        </v-chip>
                        <v-chip v-if="item.statusTitle"
                                :style="{ backgroundColor: item.statusColor, color: 'white' }"
                                size="small"
                                label>
                            {{ item.statusTitle }}
                        </v-chip>
                    </v-card-title>

                    <v-card-text>
                        <div class="d-flex flex-column ga-2">
                            <div class="d-flex justify-space-between align-center">
                                <MiniMap v-if="item.drawingThumbnail || item.drawingUrl"
                                         :thumbnail-url="item.drawingThumbnail"
                                         :x="pinFor(item).x"
                                         :y="pinFor(item).y"
                                         :pin-units="pinFor(item).units"
                                         :drawing-title="item.drawingTitle"
                                         :drawing-url="item.drawingUrl"
                                         :location-title="item.locationTitle" />
                                <v-img v-if="item.photo || item.responsePhoto"
                                       :src="item.photo || item.responsePhoto"
                                       width="72"
                                       height="72"
                                       :aspect-ratio="1"
                                       contain
                                       eager
                                       class="rounded cursor-pointer"
                                       @click="openPhotoDialog(item)" />
                            </div>

                            <div class="d-flex justify-space-between">
                                <span class="text-subtitle-2">{{ t('details') }}:</span>
                                <span>{{ [item.assetSystem, item.assetType, item.assetCategory].filter(Boolean).join(' / ') }}</span>
                            </div>

                            <div class="d-flex justify-space-between">
                                <span class="text-subtitle-2">{{ t('createdBy') }}:</span>
                                <span>{{ [item.createdByName, formatDate(item.createdDate)].filter(Boolean).join(' · ') || emptyValue }}</span>
                            </div>

                            <div class="d-flex justify-space-between">
                                <span class="text-subtitle-2">{{ t('nextInspection') }}:</span>
                                <span>{{ item.nextInspectionDate ? formatDate(item.nextInspectionDate) : emptyValue }}</span>
                            </div>

                            <div class="d-flex justify-space-between">
                                <span class="text-subtitle-2">{{ t('itemsCount', { count: item.itemCount }) }}</span>
                                <span>{{ item.locationTitle || emptyValue }}</span>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </template>
        </BaseGrid>
    </div>
</template>

<script setup>
import { ref, computed, inject, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { differenceInDays, startOfDay } from 'date-fns';
import BaseGrid from '@/components/grids/base/BaseGrid.vue';
import MiniMap from '@/components/grids/MiniMap.vue';
import { statusColors } from '@/utils/vizPalette';
import { formatCurrency, formatDate, toNumberOrNull } from '@/utils/formatters';

const props = defineProps({
    // Which columns this tab needs. The table always lists UNIQUE ASSETS.
    preset: {
        type: String,
        default: 'overview',
        validator: (value) => ['overview', 'inspection', 'data'].includes(value),
    },
});

const store = useStore();
const { t } = useI18n();

const settings = computed(() => store.state.settings);
const buildMode = computed(() => store.state.buildMode);
const hasAssetField = computed(() => store.getters.hasAssetField);

// Passing the shared filter context makes BaseGrid reload whenever any visual
// cross-filters (it watches filtersLastUpdated). The filter itself is applied by
// the filteredAssets view, which INNER JOINs filter_context - so no extra SQL here.
const filterContext = computed(() => store.state.filterContext);

const launchUrl = inject('$launchUrl', (url) => {
    console.warn('$launchUrl not injected - falling back to window.open:', url);
    window.open(url, '_blank');
});

const baseGrid = ref(null);

const showPhotoDialog = ref(false);
const selectedPhotos = ref([]);
const currentPhotoIndex = ref(0);

const emptyValue = '—';
const overdueColor = statusColors.serious;

// Column sets per tab. Optional columns drop out when the project does not
// populate the underlying field.
const COLUMN_PRESETS = {
    overview: ['assetRefId', 'miniMap', 'photo', 'drawingTitle', 'createdByName', 'assetSystem', 'manufacturer', 'responsibleParty', 'statusTitle'],
    inspection: ['assetRefId', 'miniMap', 'drawingTitle', 'assetSystem', 'latestInspectionDate', 'nextInspectionDate', 'rating', 'statusTitle'],
    data: ['assetRefId', 'photo', 'assetSystem', 'manufacturer', 'installationCost', 'latestInspectionDate', 'passRate', 'statusTitle'],
};

const COLUMN_DEFS = {
    assetRefId: { titleKey: 'assetId', key: 'assetRefId', align: 'start', sortable: true },
    miniMap: { titleKey: 'miniMap', key: 'miniMap', align: 'start', sortable: false },
    photo: { titleKey: 'photo', key: 'photo', align: 'center', sortable: false, field: 'photo' },
    drawingTitle: { titleKey: 'drawing', key: 'drawingTitle', align: 'start', sortable: true, width: '180px' },
    createdByName: { titleKey: 'createdBy', key: 'createdByName', align: 'start', sortable: true, width: '150px' },
    assetSystem: { titleKey: 'details', key: 'assetSystem', align: 'start', sortable: true, width: '200px' },
    manufacturer: { titleKey: 'manufacturer', key: 'manufacturer', align: 'start', sortable: true, width: '180px', field: 'manufacturer' },
    responsibleParty: { titleKey: 'responsibleParty', key: 'responsibleParty', align: 'start', sortable: true, width: '150px' },
    latestInspectionDate: { titleKey: 'latestInspection', key: 'latestInspectionDate', align: 'start', sortable: true, field: 'inspectionDate' },
    nextInspectionDate: { titleKey: 'nextInspection', key: 'nextInspectionDate', align: 'start', sortable: true, field: 'nextInspectionDate' },
    installationCost: { titleKey: 'installationCost', key: 'installationCost', align: 'start', sortable: true, field: 'installationCost' },
    rating: { titleKey: 'rating', key: 'rating', align: 'start', sortable: true, field: 'rating' },
    passRate: { titleKey: 'passRate', key: 'passRate', align: 'start', sortable: true, field: 'passRate' },
    statusTitle: { titleKey: 'status', key: 'statusTitle', align: 'start', sortable: true, width: '160px' },
};

const headers = computed(() =>
    (COLUMN_PRESETS[props.preset] || COLUMN_PRESETS.overview)
        .map((key) => COLUMN_DEFS[key])
        .filter((column) => column && (!column.field || hasAssetField.value(column.field)))
);

const BASE_FROM = `
        FROM filteredAssets a
        LEFT JOIN status_templates st ON a.assetStatusId = st.Id
        LEFT JOIN locations l ON a.assetLocationId = l.Id
        LEFT JOIN drawings d ON l.DrawingId = d.Id
        LEFT JOIN users u ON a.assetCreatedByUserId = u.Id`;

const SELECT_FIELDS = `
            a.assetKey AS id,
            a.assetKey AS assetKey,
            a.currentItemId AS currentItemId,
            a.assetClientId AS assetClientId,
            a.assetRefId AS assetRefId,
            a.assetSystem AS assetSystem,
            a.assetType AS assetType,
            a.assetCategory AS assetCategory,
            a.priority AS priority,
            a.responsibleParty AS responsibleParty,
            a.manufacturer AS manufacturer,
            a.model AS model,
            a.serialNumber AS serialNumber,
            a.photo AS photo,
            a.responsePhoto AS responsePhoto,
            a.signature AS signature,
            a.createdDate AS createdDate,
            a.nextInspectionDate AS nextInspectionDate,
            a.latestInspectionDate AS latestInspectionDate,
            a.installationCost AS installationCost,
            a.rating AS rating,
            a.passRate AS passRate,
            a.itemCount AS itemCount,
            a.closedCount AS closedCount,
            a.isOverdue AS isOverdue,
            a.isCommissioned AS isCommissioned,
            st.Title AS statusTitle,
            st.Color AS statusColor,
            u.Name AS createdByName,
            l.Title AS locationTitle,
            l.X AS locationX,
            l.Y AS locationY,
            d.Title AS drawingTitle,
            d.ThumbnailUrl AS drawingThumbnail,
            d.Url AS drawingUrl`;

// Only these sort keys reach the SQL - anything else falls back to the default,
// which also keeps the interpolated ORDER BY safe.
const SORT_COLUMN_MAP = {
    assetSystem: 'a.assetSystem',
    manufacturer: 'a.manufacturer',
    responsibleParty: 'a.responsibleParty',
    drawingTitle: 'd.Title',
    createdByName: 'u.Name',
    createdDate: 'a.createdDate',
    nextInspectionDate: 'a.nextInspectionDate',
    latestInspectionDate: 'a.latestInspectionDate',
    installationCost: 'a.installationCost',
    passRate: 'a.passRate',
    rating: 'a.rating',
    statusTitle: 'st.Title'
};

// Mock strings carry no apostrophes, but a user-typed term can - double up any
// single quote so the interpolated LIKE block cannot break out of the literal.
const escapeSqlLiteral = (value) => String(value).replace(/'/g, "''");

const buildWhereClause = (search) => {
    if (!search?.trim()) return '';

    const term = escapeSqlLiteral(search.trim());
    return `WHERE (
            a.assetRefId ILIKE '%${term}%'
            OR a.assetType ILIKE '%${term}%'
            OR a.assetSystem ILIKE '%${term}%'
            OR a.assetCategory ILIKE '%${term}%'
            OR a.manufacturer ILIKE '%${term}%'
            OR a.model ILIKE '%${term}%'
            OR a.serialNumber ILIKE '%${term}%'
            OR a.responsibleParty ILIKE '%${term}%'
            OR u.Name ILIKE '%${term}%'
            OR l.Title ILIKE '%${term}%'
            OR d.Title ILIKE '%${term}%'
            OR st.Title ILIKE '%${term}%'
        )`;
};

const buildOrderByClause = (sort) => {
    const direction = String(sort?.order).toLowerCase() === 'desc' ? 'DESC' : 'ASC';
    const column = SORT_COLUMN_MAP[sort?.key];

    if (column) {
        return `ORDER BY ${column} ${direction} NULLS LAST`;
    }

    // Asset refs are "<PREFIX>-<NUMBER>" (e.g. AHU-B2-10). Split on the LAST
    // hyphen and sort prefix first, then numerically, so AHU-2 < AHU-10.
    const prefixExpr = `regexp_replace(a.assetRefId, '-[^-]*$', '')`;
    const numberExpr = `TRY_CAST(regexp_extract(a.assetRefId, '-([^-]*)$', 1) AS BIGINT)`;
    return `ORDER BY ${prefixExpr} ${direction} NULLS LAST, ${numberExpr} ${direction} NULLS LAST`;
};

const transformItems = (rawItems) => rawItems.map(row => ({
    ...row,
    // BigInt/Decimal columns arrive as non-plain values - normalise for the pins
    locationX: toNumberOrNull(row.locationX),
    locationY: toNumberOrNull(row.locationY),
    passRate: toNumberOrNull(row.passRate),
    installationCost: toNumberOrNull(row.installationCost),
    itemCount: Number(row.itemCount) || 0,
    closedCount: Number(row.closedCount) || 0,
    isOverdue: Boolean(row.isOverdue),
    isCommissioned: Boolean(row.isCommissioned)
}));

const queryBuilder = async ({ page, itemsPerPage, sort, search }) => {
    const whereClause = buildWhereClause(search);
    const orderByClause = buildOrderByClause(sort);
    const offset = (page - 1) * itemsPerPage;

    const countQuery = `
        SELECT COUNT(DISTINCT a.assetKey) AS total
        ${BASE_FROM}
        ${whereClause}
    `;

    const itemsQuery = `
        SELECT ${SELECT_FIELDS}
        ${BASE_FROM}
        ${whereClause}
        ${orderByClause}
        LIMIT ${itemsPerPage}
        OFFSET ${offset}
    `;

    return { countQuery, itemsQuery, transformItems };
};

// --- per-asset map pins -------------------------------------------------
// The pivot carries no x/y, so the live app asks the search endpoint for the
// visible page's items. Until that resolves (and always in demo), the pin
// falls back to the asset's location marker.
const pinOverrides = ref(new Map());
let pinRequest = 0;

const pinFor = (item) => {
    const override = pinOverrides.value.get(item.assetKey);
    if (override) return { x: override.x, y: override.y, units: 'pixels' };
    return { x: item.locationX, y: item.locationY, units: 'normalised' };
};

const fetchPins = async (items) => {
    if (settings.value.projectCode === 'DEMO' || items.length === 0) return;

    const requestId = ++pinRequest;
    const ids = items.map((item) => item.currentItemId).filter(Boolean);
    if (ids.length === 0) return;

    try {
        const query = ids.map((id) => `ids=${encodeURIComponent(id)}`).join('&');
        const response = await store.state.apiClient.get(
            `/api/${settings.value.projectCode}/issues/search?${query}&fields=id%2Cx%2Cy&pageSize=${ids.length}`
        );
        // A newer page started loading while this was in flight.
        if (requestId !== pinRequest) return;

        const rows = Array.isArray(response) ? response : (response?.items || []);
        const byItemId = new Map(rows.map((row) => [row.id, row]));
        const next = new Map();
        items.forEach((item) => {
            const match = byItemId.get(item.currentItemId);
            if (match && Number.isFinite(Number(match.x)) && Number.isFinite(Number(match.y))) {
                next.set(item.assetKey, { x: Number(match.x), y: Number(match.y) });
            }
        });
        pinOverrides.value = next;
    } catch (error) {
        // Pins are an enhancement; the location marker still renders.
        console.warn('Could not fetch per-asset map pins:', error.message || error);
    }
};

const handleItemsLoaded = ({ items }) => {
    fetchPins(items);
};

// Helpers
const isPriority = (item) => String(item.priority).toLowerCase() === 'true';

const formatPassRate = (passRate) => (passRate === null || passRate === undefined ? emptyValue : `${Math.round(passRate)}%`);

const daysText = (item) => {
    if (!item?.nextInspectionDate) return '';
    const dueDate = new Date(item.nextInspectionDate);
    if (isNaN(dueDate.getTime())) return '';
    const daysLeft = differenceInDays(startOfDay(dueDate), startOfDay(new Date()));
    if (daysLeft < 0) return t('daysOverdue', { days: Math.abs(daysLeft) });
    return t('daysLeft', { days: daysLeft });
};

const openPhotoDialog = (item) => {
    selectedPhotos.value = [item.photo, item.responsePhoto, item.signature].filter(Boolean);
    currentPhotoIndex.value = 0;
    showPhotoDialog.value = true;
};

const openItemUrl = (item) => {
    // Demo data has no RDrive project behind it - nothing to deep link to.
    if (settings.value.projectCode === 'DEMO') return;

    const baseUrl = settings.value.apiUrl?.startsWith('api-')
        ? settings.value.apiUrl.slice(4)
        : settings.value.apiUrl;
    const url = `https://${baseUrl}/${settings.value.projectCode}/assets?asset-client-ids=${item.assetClientId}`;

    if (buildMode.value === 'production') {
        launchUrl(url);
    } else {
        window.open(url, '_blank');
    }
};

watch(
    () => store.getters.getDataLoaded,
    (loaded) => {
        if (baseGrid.value && loaded) {
            baseGrid.value.loadItems({ page: 1 });
        }
    },
    { immediate: true }
);
</script>

<style scoped>
.mini-map-placeholder {
    width: 96px;
    height: 72px;
    border-radius: 6px;
    background-color: rgba(127, 127, 127, 0.08);
    border: 1px dashed rgba(127, 127, 127, 0.35);
}

.asset-photo {
    border: 1px solid rgba(127, 127, 127, 0.25);
}
</style>
